import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, Row, Col, Table, Badge, Spinner, Alert, Button, Form } from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";

const STATUS_VARIANT = {
  Open: "secondary",
  "Pending Approval": "warning",
  "Pending Verification": "warning",
  Approved: "success",
  Denied: "danger",
};

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
];

// "6/20/2026 - 2:30 PM" from an ISO datetime, or null if unparseable.
function formatApptDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString(undefined, { year: "numeric", month: "numeric", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${date} - ${time}`;
}

export default function IntakeWorkerDashboardPage() {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [completingId, setCompletingId] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);

  const loadData = useCallback(() => {
    return api
      .get("/dashboard/intake-worker", { params: showCompleted ? { show_completed: 1 } : {} })
      .then((res) => setData(res.data))
      .catch(() => setError("Failed to load dashboard data."));
  }, [showCompleted]);

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  const handleComplete = async (taskId) => {
    setCompletingId(taskId);
    try {
      await api.put(`/tasks/${taskId}/complete`);
      await loadData();
    } catch {
      setError("Failed to mark the task complete. Please try again.");
    } finally {
      setCompletingId(null);
    }
  };

  if (loading)
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  if (error) return <Alert variant="danger">{error}</Alert>;

  const { pending_tasks, metrics } = data;

  return (
    <div>
      <h4 className="mb-4">Welcome, {user?.full_name}</h4>

      <Row className="g-3 mb-4">
        {PERIODS.map(({ key, label }) => {
          const m = metrics[key] || {};
          return (
            <Col key={key} xs={12} sm={6} xl={3}>
              <Card className="h-100 shadow-sm">
                <Card.Header className="fw-semibold bg-light">{label}</Card.Header>
                <Card.Body className="py-2 px-3">
                  <div className="d-flex justify-content-between border-bottom py-2">
                    <span className="text-muted small">Services Given</span>
                    <span className="fw-bold">{m.services_given ?? 0}</span>
                  </div>
                  <div className="d-flex justify-content-between border-bottom py-2">
                    <span className="text-muted small">Clients Helped</span>
                    <span className="fw-bold">{m.clients_helped ?? 0}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2">
                    <span className="text-muted small">Intakes Created</span>
                    <span className="fw-bold">{m.intakes_created ?? 0}</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card className="shadow-sm">
        <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
          <span>
            My Pending Tasks
            {pending_tasks.length > 0 && (
              <Badge bg="warning" text="dark" className="ms-2">
                {pending_tasks.length}
              </Badge>
            )}
          </span>
          <Form.Check
            type="switch"
            id="show-completed-toggle"
            label="Show Completed"
            className="fw-normal small mb-0"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
          />
        </Card.Header>
        <Card.Body className="p-0">
          {pending_tasks.length === 0 ? (
            <p className="text-muted p-3 mb-0">No pending tasks assigned to you.</p>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Type</th>
                  <th>Client</th>
                  <th>Description</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pending_tasks.map((item) => {
                  const rowKey = item.type === "task"
                    ? `task-${item.task_id}`
                    : `svc-${item.service_id}`;
                  return (
                    <tr key={rowKey}>
                      <td>
                        {item.type === "task"
                          ? <Badge bg="info" text="dark">Task</Badge>
                          : <Badge bg="secondary">Service</Badge>
                        }
                      </td>
                      <td>
                        <Link to={`/clients/${item.client_id}/view`}>
                          {item.client_name}
                        </Link>
                      </td>
                      <td>
                        {item.type === "task"
                          ? <span className="fst-italic">{item.task_text}</span>
                          : item.service_type === "Transportation" && item.appointment_datetime
                            ? `Transportation - ${formatApptDateTime(item.appointment_datetime)}`
                            : item.service_type
                        }
                      </td>
                      <td>{item.service_date}</td>
                      <td>
                        {item.status === "Completed"
                          ? <span className="badge" style={{ backgroundColor: "#6f42c1", color: "#fff" }}>Complete</span>
                          : item.status === "Pending"
                          ? <span className="badge" style={{ backgroundColor: "#fd7e14", color: "#fff" }}>Pending</span>
                          : <Badge
                              bg={STATUS_VARIANT[item.status] || "secondary"}
                              text={STATUS_VARIANT[item.status] === "warning" ? "dark" : undefined}
                            >
                              {item.status}
                            </Badge>
                        }
                      </td>
                      <td className="text-end">
                        {item.type === "task" && item.status === "Open" && (
                          <Button
                            size="sm"
                            variant="outline-success"
                            disabled={completingId === item.task_id}
                            onClick={() => handleComplete(item.task_id)}
                          >
                            {completingId === item.task_id
                              ? <Spinner animation="border" size="sm" />
                              : <><i className="bi bi-check2-circle me-1" />Mark Complete</>}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
